import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { fetchPriorities } from '@/lib/priorityFeed';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import ModuleShell from '@/components/layout/ModuleShell';
import FirstRunChecklist from '@/components/FirstRunChecklist';
import {
  AlertTriangle, Clock, ArrowRight, Shield, DollarSign, Briefcase,
  FileText, CheckCircle, Zap, TrendingUp, RefreshCw, Activity, Gauge, Eye
} from 'lucide-react';

const PRIORITY_CONFIG = {
  critical: { color: '#E24B4A', bg: 'rgba(226,75,74,0.08)', icon: AlertTriangle, label: 'Critical' },
  high: { color: '#E8A020', bg: 'rgba(232,160,32,0.08)', icon: Clock, label: 'High' },
  medium: { color: '#00B4D8', bg: 'rgba(0,180,216,0.08)', icon: Zap, label: 'Medium' },
  low: { color: '#5C6B7A', bg: 'rgba(92,107,122,0.06)', icon: Shield, label: 'Low' },
  info: { color: '#1D9E75', bg: 'rgba(29,158,117,0.06)', icon: CheckCircle, label: 'Info' },
};

const DECISION_COLORS = {
  APPROVE: '#1D9E75', CONDITIONS: '#00B4D8', REFER: '#E8A020', DECLINE: '#E24B4A',
};

const fmtM = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${Math.round(v || 0).toLocaleString()}`;

function HeroKPI({ label, value, sub, icon: Icon, color, testid }) {
  return (
    <Card className="p-4 rounded-sm relative overflow-hidden micro-hover" style={{ border: '1px solid rgba(136,150,168,0.18)' }} data-testid={testid}>
      <div className="absolute top-0 left-0 w-[3px] h-full" style={{ background: color }} />
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: '#5C6B7A', fontFamily: 'Georgia, serif' }}>{label}</p>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="text-3xl font-light tracking-tight mt-1.5" style={{ color: '#0D1B2A', fontVariantNumeric: 'tabular-nums' }}>{value}</p>
      {sub && <p className="text-[10px] mt-1" style={{ color: '#64748B' }}>{sub}</p>}
    </Card>
  );
}

function SecondaryKPI({ label, value, color, testid }) {
  return (
    <Card className="px-4 py-2.5 rounded-sm" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: '#5C6B7A', fontFamily: 'Georgia, serif' }}>{label}</p>
      <p className="text-lg font-medium mt-0.5" style={{ color, fontVariantNumeric: 'tabular-nums' }}>{value}</p>
    </Card>
  );
}

function DashboardKPIs() {
  const api = useApi();
  const [kpis, setKpis] = useState(null);
  useEffect(() => {
    api.get('/dashboard/kpis').then(r => setKpis(r.data)).catch(err => console.error('API error:', err?.message));
  }, [api]);

  if (!kpis) return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;

  const lr = kpis.loss_ratio_incurred ?? 0;
  const lrColor = lr > 80 ? '#E24B4A' : lr > 60 ? '#E8A020' : '#1D9E75';
  return (
    <div className="space-y-3">
      {/* Hero KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hero-kpis">
        <HeroKPI label="Gross Written Premium" value={fmtM(kpis.total_premium)} sub={`${kpis.total_policies?.toLocaleString()} active policies`} icon={DollarSign} color="#1A4C7C" testid="kpi-hero-gwp" />
        <HeroKPI label="Active Policies" value={kpis.total_policies?.toLocaleString()} sub={`${kpis.insureds?.toLocaleString()} insureds`} icon={Briefcase} color="#1D9E75" testid="kpi-hero-policies" />
        <HeroKPI label="Open Claims" value={kpis.open_claims?.toLocaleString()} sub={`$${Math.round((kpis.open_reserves || 0) / 1000).toLocaleString()}K outstanding reserves`} icon={Activity} color="#E8A020" testid="kpi-hero-claims" />
        <HeroKPI label="Loss Ratio (incurred)" value={`${lr}%`} sub="Incurred vs earned premium" icon={Gauge} color={lrColor} testid="kpi-hero-lr" />
      </div>
      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="secondary-kpis">
        <SecondaryKPI label="Pending Quotes" value={kpis.pending_quotes?.toLocaleString()} color="#00B4D8" testid="kpi-pending-quotes" />
        <SecondaryKPI label="Renewals Due (60d)" value={kpis.renewals_due_60d?.toLocaleString()} color="#E8A020" testid="kpi-renewals-due" />
        <SecondaryKPI label="Straight-Through Decisions" value={kpis.stp_rate == null ? 'no real intake yet' : `${kpis.stp_rate}%`} color="#1D9E75" testid="kpi-stp-rate" />
        <SecondaryKPI label="Avg Score" value={`${kpis.average_score?.toLocaleString()}/10K`} color="#1A4C7C" testid="kpi-avg-score" />
      </div>
      {kpis.loss_ratio_basis && (
        <p className="text-[10px] leading-relaxed" style={{ color: '#8896A8' }} data-testid="kpi-basis-note">
          {kpis.loss_ratio_basis} Earned premium {fmtM(kpis.earned_premium)} of {fmtM(kpis.total_premium)} written.
        </p>
      )}
    </div>
  );
}

// Quick actions a role may be offered (mirrors App.js ROLE_ROUTES / ROLE_DENY): nothing that creates work for a read-only login
const ACTION_ROLES = {
  '/new-business': ['platform_owner', 'mga_admin', 'underwriter'],
  '/underwriting': ['platform_owner', 'mga_admin', 'underwriter'],
  '/renewals': ['platform_owner', 'mga_admin', 'underwriter', 'regulator'],
  '/claims': ['platform_owner', 'mga_admin', 'underwriter', 'claims_handler', 'regulator'],
  '/portfolio': ['platform_owner', 'mga_admin', 'underwriter', 'actuary', 'regulator', 'investor'],
};

function QuickActionsBar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ACTIONS = [
    { label: 'New Submission', path: '/new-business?tab=submit', icon: FileText, primary: true },
    { label: 'Quote & Bind', path: '/new-business?tab=quote', icon: Zap },
    { label: 'Referrals', path: '/underwriting', icon: AlertTriangle },
    { label: 'Renewals', path: '/renewals', icon: RefreshCw },
    { label: 'Claims', path: '/claims', icon: Shield },
    { label: 'Portfolio', path: '/portfolio', icon: TrendingUp },
  ].filter(a => (ACTION_ROLES[a.path.split('?')[0]] || []).includes(user?.role));
  return (
    <div className="flex gap-2 flex-wrap" data-testid="quick-actions-bar">
      {ACTIONS.map(a => (
        <button key={a.label} onClick={() => navigate(a.path)} data-testid={`quick-action-${a.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
          className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-colors"
          style={a.primary
            ? { background: '#1A4C7C', color: '#FFFFFF' }
            : { color: '#1A4C7C', border: '1px solid rgba(26,76,124,0.2)', background: '#FFFFFF' }}>
          <a.icon className="w-3.5 h-3.5" /> {a.label}
        </button>
      ))}
    </div>
  );
}

function RecentSubmissions() {
  const api = useApi();
  const navigate = useNavigate();
  const [subs, setSubs] = useState(null);
  useEffect(() => {
    api.get('/submissions?limit=5').then(r => setSubs(r.data)).catch(() => setSubs([]));
  }, [api]);

  return (
    <div data-testid="recent-submissions">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[11px] uppercase tracking-[0.15em]" style={{ color: '#5C6B7A', fontFamily: 'Georgia, serif' }}>Recent Submissions</h2>
        <button onClick={() => navigate('/new-business?tab=pipeline')} className="text-xs font-medium hover:underline flex items-center gap-1" style={{ color: '#1A4C7C' }} data-testid="recent-submissions-view-all">
          View pipeline <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      {!subs ? <Skeleton className="h-40 rounded-md" /> : subs.length === 0 ? (
        <Card className="p-6 text-center rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
          <p className="text-xs" style={{ color: '#5C6B7A' }}>No submissions yet — start one from Quick Actions above.</p>
        </Card>
      ) : (
        <Card className="rounded-md divide-y divide-[rgba(136,150,168,0.1)] overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
          {subs.slice(0, 5).map(s => {
            const decision = s.decision || (s.status || '').toUpperCase();
            const dColor = DECISION_COLORS[s.decision] || '#5C6B7A';
            const premium = s.marais_quote?.total_payable ?? s.scoring_result?.marais_quote?.total_payable ?? s.premium;
            return (
              <button key={s.submission_id} onClick={() => navigate(`/submissions/${s.submission_id}`)}
                className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[#F5F7FA] transition-colors text-left"
                data-testid={`recent-submission-${s.submission_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: '#0D1B2A' }}>{s.business_name}</p>
                  <p className="text-[10px] font-mono" style={{ color: '#64748B' }}>
                    {s.submission_id?.slice(0, 8)} · {s.created_at ? new Date(s.created_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
                {s.composite_score != null && (
                  <span className="text-xs font-mono font-semibold shrink-0" style={{ color: '#1A4C7C' }}>{s.composite_score?.toLocaleString()}</span>
                )}
                {premium != null && (
                  <span className="text-xs font-mono shrink-0 hidden md:inline" style={{ color: '#5C6B7A' }}>${Math.round(premium).toLocaleString()}</span>
                )}
                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0" style={{ background: `${dColor}18`, color: dColor }}>{decision}</span>
              </button>
            );
          })}
        </Card>
      )}
    </div>
  );
}

function PriorityList() {
  const api = useApi();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPriorities(api).then(p => setData(p))
      .catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;

  return (
    <div>
      <h2 className="text-[11px] uppercase tracking-[0.15em] mb-3" style={{ color: '#5C6B7A', fontFamily: 'Georgia, serif' }}>
        Priorities
        {data?.count > 0 && <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(226,75,74,0.1)', color: '#E24B4A', fontFamily: "'IBM Plex Sans', sans-serif" }}>{data.count}</span>}
      </h2>

      {(!data || data.items?.length === 0) ? (
        <Card className="p-8 text-center rounded-md" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
          <CheckCircle className="w-8 h-8 mx-auto mb-3" style={{ color: '#1D9E75' }} />
          <p className="text-sm font-medium" style={{ color: '#0D1B2A' }}>Nothing urgent right now</p>
          <p className="text-xs mt-1" style={{ color: '#5C6B7A' }}>The platform monitors your queue continuously. New priorities will appear here.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.items.map((item, i) => {
            const cfg = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.medium;
            const Icon = cfg.icon;
            return (
              <button key={`${item.type}-${i}`} onClick={() => navigate(item.path)}
                className="w-full text-left group" data-testid={`priority-${item.type}`}>
                <Card className="p-3.5 rounded-md transition-all duration-150 hover:shadow-sm"
                  style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${cfg.color}` }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: cfg.bg }}>
                      <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" style={{ color: '#0D1B2A' }}>{item.title}</p>
                      <p className="text-xs" style={{ color: '#5C6B7A' }}>{item.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: cfg.bg, color: cfg.color }}>{item.module}</span>
                      <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#5C6B7A' }} />
                    </div>
                  </div>
                </Card>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CEODigest() {
  const api = useApi();
  const navigate = useNavigate();
  const [runs, setRuns] = useState(null);
  const [digestStatus, setDigestStatus] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get('/simulation/runs?limit=2').then(r => setRuns(r.data)).catch(() => setRuns([]));
    api.get('/ceo-digest/status').then(r => setDigestStatus(r.data)).catch(() => {});
  }, [api]);
  const sendDigest = async () => {
    setSending(true);
    try {
      await api.post('/ceo-digest/send');
      const { data } = await api.get('/ceo-digest/status');
      setDigestStatus(data);
    } catch (err) { console.error('API error:', err?.message); }
    setSending(false);
  };
  if (!runs || runs.length === 0) return null;
  const [latest, prev] = runs;
  const lr = latest.portfolio?.summary?.loss_ratio_incurred ?? 0;
  const prevLr = prev?.portfolio?.summary?.loss_ratio_incurred;
  const lrDelta = prevLr != null ? Math.round((lr - prevLr) * 10) / 10 : null;
  const totalTx = ['nb', 'renewals', 'claims', 'endorsements', 'cancellations'].reduce((a, k) => {
    const s = latest[k] || {};
    return a + (s.bound ?? s.accepted ?? s.created ?? s.processed ?? 0);
  }, 0);
  const calibrations = latest.portfolio?.calibrations || [];
  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)', background: 'linear-gradient(135deg, #FAFBFC, #F0F6FA)' }} data-testid="ceo-digest">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4" style={{ color: '#00B4D8' }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Daily Digest — {latest.date}</h3>
        </div>
        <div className="flex items-center gap-3">
          {digestStatus?.configured && (
            <button onClick={sendDigest} disabled={sending} data-testid="send-digest-btn"
              className="text-[10px] font-medium px-2 py-1 rounded transition-colors hover:opacity-80 disabled:opacity-50"
              style={{ background: 'rgba(29,158,117,0.1)', color: '#1D9E75' }}
              title={digestStatus.last?.sent ? `Last emailed ${new Date(digestStatus.last.sent_at).toLocaleString('en-AU')} → ${digestStatus.recipient}` : `Emails daily 07:00 AEST → ${digestStatus.recipient}`}>
              {sending ? 'Sending…' : digestStatus.last?.au_date === new Date(Date.now() + 10 * 3600 * 1000).toISOString().slice(0, 10) && digestStatus.last?.sent ? 'Emailed today ✓ (resend)' : 'Email digest now'}
            </button>
          )}
          <button onClick={() => navigate('/admin?tab=simulation')} className="text-xs font-medium hover:underline" style={{ color: '#1A4C7C' }} data-testid="ceo-digest-link">
            Full simulation report <ArrowRight className="w-3 h-3 inline" />
          </button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div><p style={{ color: '#5C6B7A' }}>Transactions processed</p><p className="font-mono font-bold text-sm" style={{ color: '#0D1B2A' }}>{totalTx}</p></div>
        <div><p style={{ color: '#5C6B7A' }}>Policies bound</p><p className="font-mono font-bold text-sm" style={{ color: '#0D1B2A' }}>{(latest.nb?.bound || 0) + (latest.renewals?.accepted || 0)}</p></div>
        <div><p style={{ color: '#5C6B7A' }}>Sim LR (incurred)</p><p className="font-mono font-bold text-sm" style={{ color: lr > 75 ? '#E24B4A' : '#1D9E75' }}>{lr}%{lrDelta != null && <span className="ml-1 text-[10px]" style={{ color: lrDelta > 0 ? '#E24B4A' : '#1D9E75' }}>({lrDelta > 0 ? '+' : ''}{lrDelta})</span>}</p></div>
        <div><p style={{ color: '#5C6B7A' }}>Calibrations applied</p><p className="font-mono font-bold text-sm" style={{ color: '#0D1B2A' }}>{calibrations.length}{calibrations.length > 0 && <span className="ml-1 text-[10px] font-normal" style={{ color: '#5C6B7A' }}>{calibrations.map(c => `${c.class} ${c.action}`).join(', ').slice(0, 60)}</span>}</p></div>
      </div>
      {latest.portfolio?.commentary && (
        <p className="mt-3 text-xs leading-relaxed line-clamp-3" style={{ color: '#3A4A5A' }}>{latest.portfolio.commentary}</p>
      )}
    </Card>
  );
}

function PlatformHealthCard() {
  const api = useApi();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    api.get('/platform/health-card').then(r => setHealth(r.data)).catch(() => {});
  }, [api]);
  if (!health) return null;
  const colors = { ok: '#1D9E75', warn: '#E8A020', fail: '#E24B4A' };
  const issues = health.checks.filter(c => c.status !== 'ok');
  return (
    <Card className="p-4 rounded-md" style={{ border: `1px solid ${health.overall === 'ok' ? 'rgba(29,158,117,0.25)' : health.overall === 'warn' ? 'rgba(232,160,32,0.35)' : 'rgba(226,75,74,0.4)'}` }} data-testid="platform-health-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => setExpanded(v => !v)} className="flex items-center gap-2" data-testid="platform-health-toggle">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: colors[health.overall], boxShadow: `0 0 6px ${colors[health.overall]}` }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Platform Health</h3>
          <span className="text-[11px] font-medium" data-testid="platform-health-overall" style={{ color: colors[health.overall] }}>
            {health.overall === 'ok' ? 'All systems go' : `${issues.length} item${issues.length > 1 ? 's' : ''} need${issues.length > 1 ? '' : 's'} attention`}
          </span>
        </button>
        <div className="flex items-center gap-2">
          {issues.some(c => c.id === 'policies' || c.id === 'sim_engine') && (
            <button onClick={() => navigate('/admin?tab=simulation')} className="text-[10px] font-semibold px-2 py-1 rounded"
              style={{ background: 'rgba(26,76,124,0.08)', color: '#1A4C7C' }} data-testid="platform-health-fix-link">
              Open growth toolkit →
            </button>
          )}
          <button onClick={async () => {
            try {
              const { data } = await api.post('/platform/health-card/test-alert');
              toast.success(data.email?.sent ? 'Health alert email sent — check your inbox' : `Not sent: ${data.email?.reason || 'unknown'}`);
            } catch { toast.error('Test alert failed'); }
          }} className="text-[10px] font-semibold px-2 py-1 rounded" data-testid="platform-health-test-alert"
            style={{ background: 'rgba(136,150,168,0.1)', color: '#5C6B7A' }}>
            Test alert email
          </button>
        </div>
      </div>
      {(expanded || issues.length > 0) && (
        <div className="mt-2.5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1" data-testid="platform-health-checks">
          {(expanded ? health.checks : issues).map(c => (
            <div key={c.id} className="flex items-center gap-2 text-xs" data-testid={`health-check-${c.id}`}>
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: colors[c.status] }} />
              <span style={{ color: '#0D1B2A' }}>{c.label}</span>
              <span className="ml-auto font-mono text-[10px] text-right" style={{ color: c.status === 'ok' ? '#5C6B7A' : colors[c.status] }}>{c.detail}</span>
            </div>
          ))}
        </div>
      )}
      {!expanded && issues.length === 0 && (
        <p className="mt-1 text-[10px]" style={{ color: '#5C6B7A' }}>All {health.checks.length} checks passing — click to view</p>
      )}
    </Card>
  );
}

function StaleSourcesNudge() {
  const api = useApi();
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    api.get('/dean/sources').then(r => setCounts({ stale: r.data.stale, flagged: r.data.flagged })).catch(() => {});
  }, [api]);
  if (!counts || (counts.stale === 0 && counts.flagged === 0)) return null;
  return (
    <button onClick={() => navigate('/knowledge?tab=sources')} className="w-full text-left" data-testid="stale-sources-nudge">
      <Card className="p-3.5 rounded-md flex items-center gap-3 transition-all hover:shadow-sm"
        style={{ border: '1px solid rgba(232,160,32,0.4)', borderLeft: '3px solid #E8A020', background: '#FDF9EF' }}>
        <Eye className="w-4 h-4 shrink-0" style={{ color: '#8A6D1D' }} />
        <div className="flex-1 text-sm" style={{ color: '#0D1B2A' }}>
          <span className="font-semibold">Knowledge source review debt: </span>
          {counts.stale > 0 && <span>{counts.stale} source{counts.stale > 1 ? 's' : ''} stale</span>}
          {counts.stale > 0 && counts.flagged > 0 && ' · '}
          {counts.flagged > 0 && <span>{counts.flagged} flagged</span>}
          <span style={{ color: '#5C6B7A' }}> — review in the Source Watcher</span>
        </div>
        <ArrowRight className="w-3.5 h-3.5 shrink-0" style={{ color: '#8A6D1D' }} />
      </Card>
    </button>
  );
}

function DashboardContent() {
  const { user } = useAuth();
  // Platform health and the daily digest are admin-only endpoints. Asking for them as an
  // underwriter produced three refused calls on the first screen of every session.
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  return (
    <div className="space-y-5" data-testid="dashboard-content">
      <FirstRunChecklist />
      <StaleSourcesNudge />
      <DashboardKPIs />
      <QuickActionsBar />
      <RecentSubmissions />
      {isAdmin && <PlatformHealthCard />}
      {isAdmin && <CEODigest />}
      <PriorityList />
    </div>
  );
}

export default function TodayModule() {
  const tabs = [
    { id: 'priorities', label: 'Priorities', content: <DashboardContent /> },
  ];

  return (
    <ModuleShell title="Today" subtitle="What needs your attention" tabs={tabs} />
  );
}
